import { getItemsAnalytics, getTransactionAnalytics } from '@ynap/analytics';
import { productSelectors } from '@ynap/product-state';

const { getGenderFromAttributes } = productSelectors;

export const trackSizeGuideOpen = () => ({
    event: 'size and fit - open',
    effect: 'open tool',
    category: {
        primaryCategory: 'size and fit',
        subCategory: 'product details page',
    },
});

export const trackSizeGuideClose = () => ({
    event: 'size and fit - close',
    effect: 'close tool',
    category: {
        primaryCategory: 'size and fit',
        subCategory: 'product details page',
    },
});

export const trackSizeGuideComplete = () => ({
    event: 'size and fit - complete',
    effect: 'complete tool steps',
    category: {
        primaryCategory: 'size and fit',
        subCategory: 'product details page',
    },
});

export const trackSizeSelect = (analytics, sizeSku, sizeLabel, internationalSizes) => ({
    event: 'size selection',
    category: {
        primaryCategory: 'product page',
    },
    item: getItem(analytics, sizeSku, sizeLabel, null, null, internationalSizes),
});

export const trackSizeAndFitSizeSelect = () => ({
    event: 'size and fit - size selection',
    category: {
        primaryCategory: 'size and fit',
        subCategory: 'product details page',
    },
});

export const trackAccordion = (analytics, accordion) => ({
    event: `open product accordion - ${accordion}`,
    effect: 'view more content',
    category: {
        primaryCategory: 'product page',
    },
    item: getItem(analytics),
});

export const trackAccordionLinkClick = (analytics, targetURL) => ({
    event: 'product accordion - link click',
    effect: 'click on a link inside the product accordion',
    category: {
        primaryCategory: 'product page',
        subCategory: `${targetURL}`,
    },
    item: getItem(analytics),
});

export const trackSizeGuide = (analytics) => ({
    event: 'size and fitting guide',
    effect: 'new window',
    category: {
        primaryCategory: 'product page',
        subCategory: 'open window',
    },
    item: getItem(analytics),
});

export const trackImage = (analytics, interaction) => ({
    event: 'browse product images',
    effect: interaction,
    category: {
        primaryCategory: 'product page',
        subCategory: interaction,
    },
    item: getItem(analytics),
});

export const trackAddToWishlist = (analytics, sizeSku, sizeLabel, internationalSizes) => {
    const { category = {} } = analytics.page;
    return {
        event: 'wish list - add item',
        effect: 'add item to wish list',
        category: {
            primaryCategory: category.primaryCategory,
            subCategory: category.subCategory1,
        },
        item: getItem(analytics, sizeSku, sizeLabel, true, null, internationalSizes),
    };
};

export const trackAddToWishlistError = (analytics, errorType) => {
    const error = errorType === 'NO_SIZE_SELECTED' ? 'size not selected' : 'API Error';
    const { category = {} } = analytics.page;
    return {
        event: 'error - cannot add to wishList',
        effect: 'user experienced error',
        category: {
            primaryCategory: 'error',
            subCategory: category.subCategory1,
        },
        attributes: {
            errorMessage: `cannot add to wishList - ${error}`,
        },
    };
};

export const trackAddToWishlistFromMiniPDP = (analytics, sizeSku, colourPartNumber) => {
    const { category = {} } = analytics.page;
    return {
        event: 'add to wishlist',
        effect: 'update wishlist',
        category: {
            primaryCategory: category.primaryCategory,
            subCategory: 'wishlist',
        },
        item: [
            {
                productInfo: {
                    productID: colourPartNumber,
                    sku: sizeSku,
                },
            },
        ],
    };
};

export const trackViewMoreDetails = (analytics, colourPartNumber) => {
    const { category = {} } = analytics.page;

    // get product info from lineItems array if available
    const product = analytics.lineItems?.find((item) => item.productId === colourPartNumber);

    let { productInfo, category: itemCategory, attributes } = analytics?.productList?.item?.[0] ?? analytics?.item?.[0] ?? {};
    const { gender } = productInfo ?? {};
    let { preOrderFlag = false, badges = [], sustainabilityFlag, position } = attributes ?? {};

    // backwards-compatibility - some of this product info may not be available
    let colour,
        discountPercent,
        productDetails = {},
        priceDetails = {};
    if (product) {
        if (!itemCategory) {
            itemCategory = product.analyticsAttributes?.category || product.category || {};
        }

        if (badges.length === 0) {
            badges = product.badges;
        }

        const prices = product.price || {};
        colour = product.colour;
        discountPercent = prices.markedDownPercentDiscount || 0;

        productDetails = {
            designer: product.brand,
            designerID: product.brandId,
            name: product.analyticsAttributes?.name || product.nameEN || product.name,
        };

        priceDetails = {
            basePrice: prices.rawUnitPrice,
            discount: discountPercent,
            discountPrice: prices.discountAmount,
            priceWithDiscountTax: prices.rawFinalPrice,
            ...(prices.xitem_baseOrderItemPrice_Annualised_GBP ? { basePriceGDP: prices.xitem_baseOrderItemPrice_Annualised_GBP } : {}),
            currency: prices.currency,
        };
    }

    return {
        event: `view product - from ${category.subCategory1}`,
        eventEffect: 'go to product page',
        category: {
            primaryCategory: category.primaryCategory,
            subCategory: category.subCategory1,
        },
        attributes: {
            productFindingMethod: category.subCategory1,
            ...(position ? { position } : {}),
        },
        item: [
            {
                category: {
                    primaryCategory: itemCategory?.primaryCategory,
                    subCategory1: itemCategory?.subCategory1,
                    subCategory2: itemCategory?.subCategory2,
                },
                productInfo: {
                    productID: colourPartNumber,
                    ...(gender ? { gender } : {}),
                    ...productDetails,
                },
                attributes: {
                    productFindingMethod: category.subCategory1,
                    preOrderFlag,
                    badges,
                    discountPercent,
                    colour,
                    ...(sustainabilityFlag ? { sustainabilityFlag } : {}),
                },
                price: priceDetails,
            },
        ],
    };
};

export const upsellNavigation = (analytics, recommendedProduct, position, event, productFindingMethod) => {
    const gender = analytics?.product?.[0]?.productInfo?.gender;
    const { price, masterCategory } = recommendedProduct;

    return {
        event,
        effect: 'go to product page',
        category: {
            primaryCategory: 'upselling',
            subCategory: 'product page',
        },
        attributes: {
            position,
        },
        item: [
            {
                attributes: {
                    productFindingMethod,
                    crossSellItem: analytics?.product?.[0]?.productInfo?.productID,
                    sustainabilityFlag: analytics?.product?.[0]?.attributes.sustainabilityFlag,
                    badges: recommendedProduct?.badge,
                    discountPercent: price?.discountPercentage,
                    position,
                    saleFlag: price?.discountPercentage > 0 ? 'on sale' : 'full price',
                    stock: price?.availability === 'InStock' ? 'IN_STOCK' : false,
                },
                category: {
                    primaryCategory: masterCategory?.labelEN || masterCategory?.label,
                    subCategory1: masterCategory?.child?.labelEN || masterCategory?.child?.label,
                    subCategory2: masterCategory?.child?.child?.labelEN || masterCategory?.child?.child?.label,
                },
                price: {
                    basePrice: price?.originalPrice || price?.finalPrice,
                    discount: price?.discountPercentage || 0,
                    discountPrice: price?.originalPrice ? price?.originalPrice - price?.finalPrice : 0,
                    priceWithDiscountTax: price?.finalPrice,
                    currency: price?.currency?.label,
                },
                productInfo: {
                    designer: recommendedProduct?.designer,
                    designerID: recommendedProduct?.designerIdentifier,
                    name: recommendedProduct?.description,
                    productID: recommendedProduct?.partNumber,
                    ...(gender ? { gender } : {}),
                },
            },
            getItem(analytics)[0],
        ],
    };
};

export const upsellListImpression = (items, carouselType) => {
    return {
        event: 'upsell list impression',
        eventEffect: 'show upsell products',
        category: { primaryCategory: 'ecommerce', subCategory: carouselType },
        listing: {
            item: items.map(({ price, partNumber, description, designer, designerIdentifier, analyticsAttributes, badge, masterCategory }, i) => ({
                attributes: {
                    badges: badge,
                    discountPercent: price?.discountPercentage,
                    position: i + 1,
                    saleFlag: price?.discountPercentage > 0 ? 'on sale' : 'full price',
                    stock: price?.availability === 'InStock' ? 'IN_STOCK' : false,
                },
                category: {
                    primaryCategory: masterCategory?.labelEN || masterCategory?.label || analyticsAttributes?.category?.primaryCategory,
                    subCategory1: masterCategory?.child?.labelEN || masterCategory?.child?.label || analyticsAttributes?.category?.subCategory1,
                    subCategory2: masterCategory?.child?.child?.labelEN || masterCategory?.child?.child?.label || analyticsAttributes?.category?.subCategory2,
                },
                price: {
                    basePrice: price?.originalPrice || price?.finalPrice,
                    discount: price?.discountPercentage || 0,
                    discountPrice: price?.originalPrice ? price?.originalPrice - price?.finalPrice : 0,
                    priceWithDiscountTax: price?.finalPrice,
                    currency: price?.currency?.label,
                    bestPrice: price?.minimumPrice,
                },
                productInfo: {
                    designer: designer,
                    designerID: designerIdentifier,
                    name: description,
                    productID: partNumber,
                    ...(analyticsAttributes?.gender ? { gender: analyticsAttributes?.gender } : {}),
                },
            })),
        },
    };
};

export const trackAddToBag = (analytics, sizeSku, sizeSelectionLabel, quantity = 1, internationalSizes) => {
    const { category = {} } = analytics.page;
    return {
        event: 'add to cart',
        effect: 'update cart',
        category: {
            primaryCategory: category.primaryCategory,
            subCategory: category.subCategory1,
        },
        item: getItem(analytics, sizeSku, sizeSelectionLabel, true, quantity, internationalSizes),
    };
};

export const trackAddToBagError = (analytics, errorType) => {
    let error;
    const { category = {} } = analytics.page;
    switch (errorType) {
        case 'CART_ITEM_INSUFFICIENT_STOCK':
            error = 'size out of stock';
            break;
        case 'NO_SIZE_SELECTED':
            error = 'size not selected';
            break;
        default:
            error = 'API Error';
    }

    return {
        event: 'error - cannot add to cart',
        effect: 'user experienced error',
        category: {
            primaryCategory: 'error',
            subCategory: category.subCategory1,
        },
        attributes: {
            errorMessage: `cannot add to cart - ${error}`,
        },
    };
};

export const trackAddToBagFromMiniPDP = (analytics = {}, quantity = 1) => {
    const {
        page: { category = {} } = {},
        selectedProduct: { product = {}, selectedProductSize = {}, selectedProductVariant = {}, selectedProductIndex } = {},
    } = analytics;
    const gender = getGenderFromAttributes(product?.attributes);

    return {
        event: 'add to cart',
        effect: 'update cart',
        category: {
            primaryCategory: category?.primaryCategory,
            subCategory: category?.subCategory1,
        },
        attributes: {
            pageNumer: analytics.pageNumber,
            position: selectedProductIndex,
        },
        item: [
            {
                productInfo: {
                    productID: selectedProductVariant?.partNumber,
                    sku: selectedProductSize?.partNumber,
                    size: selectedProductSize?.size?.labelSize,
                    productName: product?.nameEN,
                    manufacturer: product?.designerNameEN,
                    manufacturerId: product?.designerIdentifier,
                    ...(gender ? { gender } : {}),
                },
                quantity,
                category: {
                    primaryCategory: product?.masterCategory?.labelEN || '',
                    subCategory1: product?.masterCategory?.child?.labelEN || '',
                    subCategory2: product?.masterCategory?.child?.child?.labelEN || '',
                },
                attributes: {
                    categoryIdList: [
                        product?.masterCategory?.categoryId || '',
                        product?.masterCategory?.child?.categoryId || '',
                        product?.masterCategory?.child?.child?.categoryId || '',
                    ],
                    saleFlag: product?.onSale ? 'on sale' : 'full price',
                    stock: product?.badgeType === 'STOCK' ? product?.badge.toLowerCase() : 'in stock',
                },
                price: {
                    currency: product?.price?.currency?.label.toLowerCase(), // Workaroud to handle WL v2 for TON -> remove this after v2 is completly fine => https://jira.nap/browse/ISPW-458 - products with displayable false comes without price set
                    basePrice: product?.price?.sellingPrice?.amount / product?.price?.sellingPrice?.divisor,
                },
            },
        ],
    };
};

export const getItem = (analytics, sizeSku, sizeSelectionLabel, withPrice, quantity, internationalSizes) => {
    if (!analytics?.product?.[0]) return null;

    const { productInfo, category, attributes, price } = analytics?.product?.[0];
    const { productID, productName, manufacturer, manufacturerID, gender } = productInfo;
    const { firstVisibleDate, productStock, preOrderFlag = false, badges = [], sustainabilityFlag, saleFlag } = attributes;
    const sku = productStock.find((sku) => sku.size === sizeSelectionLabel);

    return [
        {
            productInfo: {
                productID,
                productName,
                manufacturer,
                manufacturerID,
                ...(sizeSku ? { sku: sizeSku, size: sizeSelectionLabel, sizeInfo: parseSizeInfo(internationalSizes) } : {}),
                ...(gender ? { gender } : {}),
            },
            category,
            ...(quantity && { quantity }),
            attributes: {
                firstVisibleDate,
                preOrderFlag,
                ...(sku ? { stock: sku.stock } : {}),
                badges,
                sustainabilityFlag,
                saleFlag,
            },
            ...(withPrice ? { price: price } : {}),
        },
    ];
};

export const trackAddRemoveItem = (item, quantity, newQuantity) => {
    const itemAnalytics = getItemsAnalytics([item])[0] || {};
    const event = newQuantity >= quantity ? 'add more to cart' : 'remove from cart - some';

    const removeItem = {
        event,
        effect: 'update cart',
        category: {
            primaryCategory: 'ecommerce',
            subCategory: 'basket',
        },
        item: [
            {
                ...itemAnalytics,
                quantity: 1,
            },
        ],
    };

    return removeItem;
};

export const trackRemoveRecentlyViewedItem = (partNumber, price) => ({
    event: 'remove_item_recentlyviewed',
    productID: partNumber,
    price,
});

export const trackViewMoreNavigation = (analytics) => {
    const { category = {} } = analytics.page;
    return {
        event: 'upsell - similar products - to plp',
        effect: 'navigate',
        category: {
            primaryCategory: 'upselling',
            subCategory: category.subCategory1,
        },
        item: getItem(analytics),
    };
};

export const trackGoToBag = (analytics) => {
    const subCategory = analytics?.page?.category?.subCategory1 || '';

    return {
        event: `proceed to purchase - from ${subCategory}`,
        effect: 'enter payment',
        category: {
            primaryCategory: 'payment',
            subCategory,
        },
        attributes: {
            position: 'pop-up',
        },
    };
};

export const trackEmailUsCTA = (analytics, messageType) => {
    return {
        event: 'personal shopper click - email',
        effect: 'contact personal shopper',
        category: {
            primaryCategory: 'product page',
            subCategory: messageType,
        },
    };
};

export const trackCallUsCTA = (analytics) => {
    return {
        event: 'call customer care',
        effect: 'proceed calling the customer care',
        category: {
            primaryCategory: 'product page',
            subCategory: analytics?.page?.pageInfo?.pageName || '',
        },
    };
};

function parseSizeInfo(internationalSizes) {
    if (!internationalSizes) return null;
    let sizeInfo = {};
    internationalSizes.forEach((size) => {
        sizeInfo[size.name] = size.labels[0];
    });
    return sizeInfo;
}

export const trackAddToBagNonBasketProduct = (analytics, product, skuPartNumber, type) => {
    const { category = {} } = analytics?.page || {};

    return {
        event: 'add to cart',
        effect: `update cart${type ? ` - ${type}` : ''}`,
        category: {
            primaryCategory: category.primaryCategory,
            subCategory: category.subCategory1,
        },
        item: [getNonBasketProduct({ product, skuPartNumber })],
        ...(analytics ? { transaction: getTransactionAnalytics(analytics) } : {}),
    };
};

const getNonBasketProduct = ({ product, skuPartNumber }) => {
    if (!product && !product.partNumber) return {};
    const primaryCategory = product.masterCategory || {};
    const subCategory1 = primaryCategory.child || {};
    const subCategory2 = subCategory1.child || {};

    return {
        productInfo: {
            sku: skuPartNumber || product.partNumber,
            size: getSize(product.sKUs, skuPartNumber),
            productName: product.name,
            manufacturer: product.designerName,
        },
        category: {
            primaryCategory: primaryCategory.label || '',
            subCategory1: subCategory1.label || '',
            subCategory2: subCategory2.label || '',
        },
        attributes: {
            categoryIdList: [primaryCategory.identifier || '', subCategory1.identifier || '', subCategory2.identifier || ''],
            saleFlag: product.onSale ? 'on sale' : 'full price',
        },
        price: {
            currency: product.price.currency?.label.toLowerCase(),
            basePrice: product.price.finalPrice,
            baseFullPrice: product.price.originalPrice || product.price.finalPrice,
        },
    };
};

const getSize = (sKUs, partNumber) => {
    const sku = sKUs?.find((element) => {
        return element.partNumber === partNumber;
    });
    return sku?.size;
};
